<template>
  <transition name="fade">
    <div v-if="showReadingComponent" class="srl-wrap">

      <div v-if="ready" ref="responseEl" class="srl-response" :class="{ 'srl-gift': !!giftLead }">
        <button v-show="!table && !noClose" class="srl-close" aria-label="Close reading" @click="exitReading">
          <i class="fas fa-times"></i>
        </button>

        <!-- The top pane folded to the table (2026-10-21 / 2026-10-22 §8): a
             pulled card has landed. The title, the spread as mini cards and
             the pulled tiles; any tile opens the pane on that card. -->
        <TableRow v-if="table" :title="tableTitle" :cards="tableCards" :pulled="pulledTiles" @open="onTableOpen" />

        <!-- A pulled card, opened from its tile or the strip: the card, its
             phrase, the question it answered and the Mystic's reading. -->
        <section v-else-if="pulledShown" class="srl-pulled" data-testid="pulled-spotlight">
          <h1 v-if="tableTitle" class="srl-pulled-title">{{ tableTitle }}</h1>
          <nav class="srl-pulled-strip" aria-label="Positions">
            <button v-for="(c, i) in tableCards" :key="'c' + i" type="button" class="srl-pill"
                    @click="openCard(i)">{{ shortPos(c.position) }}</button>
            <button v-for="(p, k) in pulledTiles" :key="'p' + k" type="button" class="srl-pill srl-pill-pulled"
                    :class="{ sel: k === pulledOpen }" @click="pulledOpen = k">✦ {{ p.label }}</button>
          </nav>
          <div class="srl-pulled-body">
            <figure class="srl-pulled-card">
              <button type="button" class="srl-pulled-art" :aria-label="`${pulledShown.name}: what it means`"
                      @click="openPulledDetail(pulledShown)">
                <img v-if="pulledShown.image" :src="pulledShown.image" :alt="pulledShown.name" :class="{ rev: pulledShown.reversed }" />
              </button>
              <figcaption>{{ pulledShown.name }}<span v-if="pulledShown.reversed"> · Reversed</span></figcaption>
            </figure>
            <div class="srl-pulled-words">
              <h2 v-if="pulledShown.line" class="srl-pulled-line">{{ pulledShown.line }}</h2>
              <p v-if="pulledShown.question" class="srl-pulled-for">For “{{ pulledShown.question }}”</p>
              <p v-if="pulledShown.reading" class="srl-pulled-reading">{{ pulledShown.reading }}</p>
            </div>
          </div>
        </section>

        <!-- The gift room (2026-10-22 §5): the top pane IS the letter, the
             same GiftLetter the send screen's preview draws, from the same
             data. Round 2 (DESIGN-2 §39): the reply card is no longer at its
             end; there is one form, and it opens in the chat. -->
        <div v-show="!table && !pulledShown" class="srl-main">
        <div v-if="giftLead" class="srl-letter" data-testid="gift-room-letter">
          <GiftLetter tone="hers" v-bind="giftLetter" :zoomable="true" @card-detail="onLetterCardDetail" />
          <!-- The pane's edge (and the divider's handle on it) fades the paper
               out rather than cutting a line in half (gift bug walk 2026-10-25 #29). -->
          <div class="srl-letter-fade" aria-hidden="true"></div>
        </div>

        <SpreadReading
          v-else
          ref="spreadRef"
          :question="heroQuestion"
          :title="revealed ? summaryTitle : ''"
          :cards="spreadCards"
          :closing="closingCard"
          :synthesis="revealed ? readingAnalysisVal : ''"
          :gate-from="gateFrom"
          :locked="locked"
          :gate-hides-card="false"
          :show-illustration="revealed && !giftLead"
          :caption="revealed ? illustrationCaption : ''"
          :reveal-summary="revealSummaryFlag"
          :auth-gate="authGate"
          :zoomable="true"
          :extra-steps="pulledTiles.map(p => p.label)"
          @extra-step="k => (pulledOpen = k)"
          @reveal-auth="requestLogin"
          @card-detail="onCardDetail"
          @summary-visible="v => emit('summary-visible', v)"
        >
          <!-- The sign-in seal — the only seal left (2026-10-29: no payment
               seal; every reading is paid at its start). A signed-out viewer
               gets a position-aware line and Reveal Myself; never a price. -->
          <template #paywall="{ position, isSummary }">
            <div class="srl-paywall">
              <p class="srl-paywall-copy">{{ gateCopy(position, isSummary) }}</p>
              <button class="srl-pay-btn" @click="requestLogin"><i class="fas fa-sign-in"></i> Reveal Myself (Login)</button>
            </div>
          </template>

          <!-- Illustration — the real generated image (or a loading state). -->
          <template #illustration="{ loaded }">
            <button
              v-if="readingImageVal" type="button" class="srl-illus-btn"
              @click="expandImage" :aria-label="illustrationCaption ? `Enlarge the picture: ${illustrationCaption}` : 'Enlarge the picture'"
            >
              <img :src="readingImageVal" :alt="illustrationCaption || 'Reading illustration'" class="srl-illus" @load="loaded" />
            </button>
            <div v-else class="srl-illus-loading-wrap">
              <img
                src="https://mysticaidablob.blob.core.windows.net/public/static/LoadingVideoGif.gif"
                alt="Painting your vision" class="srl-illus-loading"
              />
              <transition name="srl-msg-fade" mode="out-in">
                <p :key="imageLoadingPhrase" class="srl-illus-loading-msg">{{ imageLoadingPhrase }}</p>
              </transition>
            </div>
          </template>
        </SpreadReading>
        </div>
        <!-- Stage 2 (R5.4): her own reading from the gift room is rated with
             the reading's one rating, in the chat after the summary (SP-R1);
             the page asks "What landed?" from there. Never a second rating
             here in the pane (gift bug walk 2026-10-25 #10). -->
        <!-- Post-reading actions (Talk it through / Rate / Share / Finish) live
             in the chat window's action bar, not here. -->
      </div>

      <div v-else class="srl-loading">
        <!-- Show the seeker their drawn hand, gently floating, while the reading
             weaves — far nicer than a generic spinner. -->
        <div v-if="selectedCards.length" class="srl-loading-cards">
          <div
            v-for="(c, i) in selectedCards" :key="i" class="srl-loading-card"
            :class="{ rev: !!c.reversed }"
            :style="{ animationDelay: (i * 0.18) + 's' }"
          >
            <img v-if="c.image_path || c.image_url" :src="c.image_path || c.image_url" :alt="c.name" />
            <span v-else class="srl-loading-card-glyph">✦</span>
          </div>
        </div>
        <img
          v-else
          src="https://mysticaidablob.blob.core.windows.net/public/static/LoadingVideoGif.gif"
          alt="Weaving your reading" class="srl-loading-img"
        />
        <p class="srl-loading-text">Weaving your reading…</p>
      </div>

      <!-- Expanded illustration overlay. -->
      <Teleport to="body">
        <div v-if="isImageExpanded && readingImageVal" class="srl-expanded" @click="collapseImage">
          <div class="srl-expanded-overlay"></div>
          <figure class="srl-expanded-fig">
            <img :src="readingImageVal" :alt="illustrationCaption || 'Reading illustration'" class="srl-expanded-img" />
            <figcaption v-if="illustrationCaption" class="srl-expanded-cap"><CaptionBeats :text="illustrationCaption" /></figcaption>
          </figure>
        </div>
      </Teleport>
    </div>
  </transition>
</template>

<script setup>
/**
 * SpreadReadingLive — the live, seeker-facing reading. Owns the session
 * behaviour (the sign-in seal, the summary step, share, finish, exit,
 * illustration expand). No payment gate: every reading is paid at its start
 * (2026-10-29, backend reader/service/reading_charge) and renders the card layout through the SHARED
 * <SpreadReading> component — the same one the Studio preview uses, so the two
 * can't drift. Replaces OneCardReading + FourCardReading with a single,
 * spread-agnostic surface (1 / 3 / 4 / … cards).
 */
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount } from 'vue'
import { readingLocked } from '@/utils/reading-lock'
import { requestAuth } from '@/utils/request-auth'
import { doorIntentFor } from '@/utils/auth-intents'
import { useRoute } from 'vue-router'
import { store } from '@/store'
import SpreadReading from '@/components/ui/games/SpreadReading.vue'
import CaptionBeats from '@/components/ui/share/CaptionBeats.vue'
import { openCardDetailFor } from '@/utils/card-detail'
import GiftLetter from '@/components/ui/GiftLetter.vue'
import TableRow from '@/components/ui/games/TableRow.vue'
import { roomLetter } from '@/utils/care-room'
import '@/assets/fonts/letter/letter-fonts.css'
import {
  readingTitle, readingPast, readingPresent, readingFuture, readingAdvice,
  readingAnalysis, readingImage, ReadingSummaryTitle, readingIllustrationTitle,
  readingIsReady, resetReadingState, resetAstrologyReadingState, readingPositions,
} from '@/utils/reader-conversation'

const props = defineProps({
  showReadingComponent: { type: Boolean, default: false },
  showFullReading: { type: Boolean, default: true },
  selectedCards: { type: Array, default: () => [] },
  // The active spread's TRUE position labels (e.g. The Surface / The Truth
  // Beneath / The Decipher). When provided, overrides the hardcoded
  // Past/Present/Future fallback so any spread renders its own positions.
  positionLabels: { type: Array, default: () => [] },
  // Names shown on the folded table in place of the positions (a lesson's full
  // reading: Before · Now · Ahead · What helps). The reading keeps its own
  // positionLabels as keys for its streamed text.
  displayLabels: { type: Array, default: () => [] },
  // Resolved stream mode per position (on_draw / after_summary / private),
  // parallel to positionLabels. A position revealed after the summary is the
  // reading's closing counsel and belongs to the chat, not to this pane.
  positionModes: { type: Array, default: () => [] },
  scrollToShareTrigger: { type: Boolean, default: false },
  // The top pane folded to the table row (a card was pulled, 2026-10-21).
  table: { type: Boolean, default: false },
  // The gift room's steps are open over the reading: the room's own ✕ is the
  // one (gift room walk #7, two ✕ on step 1 at 390).
  noClose: { type: Boolean, default: false },
  // The cards pulled after the reading: [{ id, label, card, image, reversed,
  // question, line, reading }] (Talk it through's, or her card in a gift room).
  talkCards: { type: Array, default: () => [] },
  // The gift room's door before her first act (ReaderPage.ensureGiftMember).
})
const emit = defineEmits(['exitReading', 'update:showLogin', 'update:scrollToShareTrigger',
  'showEmeraldShop', 'summary-visible', 'gift-rated', 'own-rated', 'rating-eligible', 'unfold'])

// The gift room's lead and rating: set by the page while a package is open
// in its room ({ short, note, from_name, portrait }); null everywhere else.
const giftLead = computed(() => (store.gift_room_open && store.gift_lead) || null)
// The letter: the server's letter for the room (or the preview the room
// fetched), the loaded panel for anything it lacks.
const giftLetter = computed(() => {
  const g = giftLead.value
  if (!g) return {}
  return roomLetter({
    letter: g.letter || null,
    gift: g.gift || {},
    loaded: {
      cards: spreadCards.value.map((c, i) => ({ ...c, phrase: lineFor(i) })),
      image: readingImageVal.value, title: summaryTitle.value, summary: readingAnalysisVal.value,
      readerAvatar: g.portrait || '',
    },
  })
})
// Her card's phrase from the loaded reading (positions[].line), by position.
function lineFor(i) {
  const lines = store.reading_position_lines || {}
  const label = positionLabelsForSpread.value[i]
  const key = label ? 'reading' + String(label).replace(/\s+/g, '') : ''
  return (key && lines[key]) || ''
}
const spreadRef = ref(null)
function onLetterCardDetail(i) {
  const c = (giftLetter.value.cards || [])[i]
  if (!c) return
  openCardDetailFor({ reader: store.current_reader_subdomain, card: c.name, reversed: c.reversed, position: c.position })
}
// ── The table row and the pulled cards (2026-10-21 talk card design) ──
const pulledOpen = ref(null)
const pulledTiles = computed(() => (props.talkCards || []).map((t) => ({
  label: t.label || 'Pulled', name: t.card || t.name || '', image: t.image || '', reversed: !!t.reversed,
  question: t.question || '', line: t.line || '', reading: t.reading || '', id: t.id,
})))
const pulledShown = computed(() => (pulledOpen.value === null ? null : pulledTiles.value[pulledOpen.value] || null))
const shortPos = (p) => String(p || '').replace(/^the\s+/i, '')
const tableTitle = computed(() => (giftLead.value ? (giftLetter.value.title || '') : '')
  || summaryTitle.value || heroQuestion.value)
// The whole table: every position (the closing card too), or in a gift room
// the letter's cards (her own pick is the Yours tile, never a fifth card).
const tableCards = computed(() => (giftLead.value
  ? (giftLetter.value.cards || []).map((c) => ({ position: c.position, name: c.name, image: c.image_url, reversed: c.reversed }))
  : allSpreadCards.value.map((c, i) => ({ position: props.displayLabels[i] || c.position, name: c.name, image: c.image, reversed: c.reversed }))))
function onTableOpen({ kind, index }) {
  emit('unfold')
  if (kind === 'pulled') { pulledOpen.value = index; return }
  openCard(index)
}
// The pane on one of the spread's cards: the stepper's step, or the letter's row.
function openCard(i) {
  pulledOpen.value = null
  nextTick(() => {
    if (giftLead.value) {
      const rows = document.querySelectorAll('[data-testid="gift-room-letter"] [data-testid="gift-letter-card"]')
      const row = rows[i]
      if (row) { try { row.scrollIntoView({ behavior: 'smooth', block: 'start' }) } catch (e) { row.scrollIntoView() } }
      return
    }
    if (spreadRef.value && spreadRef.value.pickAndScroll) spreadRef.value.pickAndScroll(i)
  })
}
function openPulledDetail(p) {
  if (!p) return
  openCardDetailFor({ reader: store.current_reader_subdomain, card: p.name, reversed: p.reversed, position: p.label })
}
// A new pulled card closes the spotlight on an older one.
watch(() => (props.talkCards || []).length, () => { if (pulledOpen.value !== null && !pulledShown.value) pulledOpen.value = null })
defineExpose({ openPulled: (k) => { pulledOpen.value = k } })
// A reading made for yourself, revealed: its one rating is rated in the CHAT, as
// the last item of the reading (2026-10-20, SP-R1). This pane no longer draws it;
// it only says whether one is due. Not on a reading made for someone until it
// went out (then the chat's end block opens it, DESIGN 2026-10-22 §4), not for a
// visitor on a shared link or a link holder, not in a gift room.
// The page adds "the chat has reached the summary" (utils/rating-at-summary).
const ratingEligible = computed(() => revealed.value && !!readingAnalysisVal.value
  && !isSharedArrival.value && !store.link_holder && !store.gift_room_open && !store.intro_people_reading
  && !(store.reading_gift && store.reading_gift.is_gift && !store.reading_gift.sent))

const route = useRoute()
// The "Reveal Your Summary" scrim is a first-reading gesture only. A reading
// opened from history (?reading=<id> in the URL) used to show its summary at
// once, which painted the weave and illustration under the spotlight and cut
// them off at the divider. It now reads like the live one: spotlight first,
// then the Summary step. (The route flag stays for the shared-arrival check.)

// A SHARED arrival = the viewer came in on a magic link (carries ?cf/?inv that
// an owner's own history click never has). For such a viewer the summary is the
// auth gate: logged OUT → show the reveal scrim and route its click to login;
// logged IN → no auth gate; the summary is a step, as on every reading.
//
// Their OWN reading started from a gift room keeps the room's address
// (?reading=<gift>&inv=<room>&own=<theirs>, utils/stage-two): ?own= says the
// reading is the viewer's, so it is not a shared arrival (its rating is due).
// The page writes ?own= quietly (history, not the router), so the route can
// still show only the room's ?inv: the page's own flag (store.intro_own_reading)
// says the same thing (e2e S18, 2026-10-30).
const isSharedArrival = computed(() => !!(route && route.query && (route.query.cf || route.query.inv)
  && !route.query.own && !store.intro_own_reading))
const loggedOut = computed(() => !store.querent_id)
const authGate = computed(() => isSharedArrival.value && loggedOut.value && !store.gift_room_open && !store.link_holder)
const revealSummaryFlag = computed(() => true)
// No resume: revealing needs the reading surface restored, which the
// post-login navigation tears down. Phase 3 territory.
// A reading made for someone opens the send door ("Send it to Maya", R3.8).
function requestReadingAuth() {
  const door = doorIntentFor(store.reading_gift)
  requestAuth(door.theme, null, { forName: door.forName || '' })
}
function requestLogin() { requestReadingAuth(); emit('update:showLogin', true) }

const isImageExpanded = ref(false)

// Mystical, rotating captions shown beneath the loading image so the seeker
// knows the illustration is being conjured (not stalled).
const IMAGE_LOADING_PHRASES = [
  'Painting your vision…',
  'Summoning imagery from the ether…',
  'The brush of fate moves across the canvas…',
  'Channeling your reading into form…',
  'Translating symbols into light…',
  'The vision is taking shape…',
]
const phraseIndex = ref(0)
const imageLoadingPhrase = computed(
  () => IMAGE_LOADING_PHRASES[phraseIndex.value % IMAGE_LOADING_PHRASES.length],
)
let phraseTimer = null
onMounted(() => { phraseTimer = setInterval(() => { phraseIndex.value++ }, 3200) })
onBeforeUnmount(() => { if (phraseTimer) clearInterval(phraseTimer); emit('rating-eligible', false) })

// ── reactive reading content (module refs from the conversation layer) ──
const ready = computed(() => readingIsReady.value)
const heroQuestion = computed(() => (readingTitle.value || '').replace(/^"(.*)"$/, '$1'))
const summaryTitle = computed(() => ReadingSummaryTitle.value || '')
const illustrationCaption = computed(() => readingIllustrationTitle.value || '')
const readingAnalysisVal = computed(() => readingAnalysis.value || '')
const readingImageVal = computed(() => readingImage.value || '')

const count = computed(() => (props.selectedCards || []).length)
const isOneCard = computed(() => count.value === 1)
// Sealed only for a signed-out viewer (utils/reading-lock). No payment seal:
// every reading is paid at its start, and an old one delivered unpaid is
// simply shown (2026-10-29).
const locked = computed(() => readingLocked({
  loggedIn: !!store.querent_id,
  giftToViewer: !!store.gift_room_open,
  linkHolder: !!store.link_holder,
}))
const revealed = computed(() => !locked.value)
// Dynamic gate, uniform across spreads: positions 0,1 (e.g. Past, Present) are
// always free-explained; positions 2+ are gated (meaning locked, face still
// shown — the seeker already saw the card in the draw). For 1- and 2-card draws
// nothing is gated here — the SUMMARY carries the gate instead (SpreadReading's
// summaryLocked). "Anything past 2 cards has that gate."
const gateFrom = computed(() => 2)

// Hardcoded fallback labels by spread size — used only when the active spread's
// real positionLabels aren't supplied (legacy readers / older payloads).
const LABELS = {
  1: ['The Focus'],
  3: ['The Past', 'The Present', 'The Future'],
  4: ['The Past', 'The Present', 'The Future', 'Tarot Advice'],
}
// Prefer the active spread's TRUE position labels; fall back to the by-count map.
const positionLabelsForSpread = computed(() => {
  if (props.positionLabels && props.positionLabels.length) return props.positionLabels
  return LABELS[count.value] || LABELS[4]
})
// Per-position reading text. The 4-card arc streams to the legacy
// readingPast/Present/Future/Advice refs; every other spread (1/3/N) accumulates
// into the generic readingPositions map keyed by reading<LabelNoSpaces>
// (e.g. readingTheSurface). Resolve from the map first, then the legacy refs.
const LEGACY_BY_INDEX = computed(
  () => [readingPast.value, readingPresent.value, readingFuture.value, readingAdvice.value],
)
function meaningForPosition(label, index) {
  // Every card (incl. the 1-card draw) shows its OWN per-card detail here — the
  // reading<Label> ref streamed during the draw. The stack_analysis is the
  // SUMMARY (footer synthesis), not a card meaning, so 1-card no longer borrows
  // it as the card's text.
  if (label) {
    const ref = 'reading' + String(label).replace(/\s+/g, '')
    const fromMap = readingPositions.value && readingPositions.value[ref]
    if (fromMap) return fromMap
  }
  return LEGACY_BY_INDEX.value[index] || ''
}
// Card tapped → open the global card-detail modal (meaning + this reader's symbolism).
function onCardDetail(index) {
  // The pane's slots are the open cards, then the closing card (its strip
  // order) — so the slot past the open cards is the closing card.
  const c = index === spreadCards.value.length ? closingCard.value : spreadCards.value[index]
  if (!c) return
  openCardDetailFor({ reader: store.current_reader_subdomain, card: c.name, reversed: c.reversed, position: c.position })
}

const allSpreadCards = computed(() => {
  const labels = positionLabelsForSpread.value
  const modes = props.positionModes || []
  return (props.selectedCards || [])
    .map((c, i) => ({
      position: labels[i] || `Card ${i + 1}`,
      name: c.name,
      image: c.image_path || c.image_url || '',
      reversed: !!c.reversed,
      // Index i is the position's TRUE index — resolve the meaning before any
      // filtering, so separating the closing card can't re-key the others.
      meaning: meaningForPosition(labels[i], i),
      _mode: modes[i],
    }))
})
// The open cards: revealed as drawn.
const spreadCards = computed(() => allSpreadCards.value.filter((c) => c._mode !== 'after_summary'))
// The closing card: revealed after the weave, in the chat below it — ONE
// place. The pane gets it as a sealed second step only when it would
// otherwise hold a single card (the Fork: Where You Stand, then The Move);
// a spread with two or more open cards already has its steps and its
// summary, and putting Advice in the pane too gave every four-card reading a
// "Reveal Advice" button it never had.
const closingCard = computed(() => (
  spreadCards.value.length === 1
    ? allSpreadCards.value.find((c) => c._mode === 'after_summary') || null
    : null
))

// The sign-in seal's line, position-aware: "To see your <position> card,
// please log in" (or the summary variant).
function gateCopy(position, isSummary) {
  const pos = String(position || '').replace(/^the\s+/i, '').trim()
  if (isSummary) return 'To see your full reading, please log in.'
  return pos ? `To see your ${pos} card, please log in.` : 'To see this card, please log in.'
}

function expandImage() { isImageExpanded.value = true }
function collapseImage() { isImageExpanded.value = false }

function exitReading() {
  readingIsReady.value = false
  readingImage.value = ''
  resetReadingState()
  resetAstrologyReadingState()
  emit('exitReading', store.current_reader_subdomain)
}
// Tell the page whether a rating is due: it draws it in the chat.
watch(ratingEligible, (v) => emit('rating-eligible', v), { immediate: true })

// The response pane's element (kept for the host's scroll fitting).
const responseEl = ref(null)
</script>

<style scoped>
/* The gift room's letter (2026-10-22 §5). */
.srl-letter { box-sizing: border-box; max-width: 780px; margin: 0 auto; padding: 12px 12px 30px; }
.srl-letter-fade { position: sticky; bottom: 0; height: 44px; margin-top: -44px; pointer-events: none;
  border-radius: 0 0 18px 18px; background: linear-gradient(180deg, rgba(244, 236, 220, 0), #f4ecdc 80%); }
/* A pulled card opened in the pane (the talk card design's P3). */
.srl-pulled {
  --display: 'Letter Display', 'Cinzel', Georgia, serif; --serif: 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  box-sizing: border-box; max-width: 900px; margin: 0 auto; padding: 18px 16px 22px; color: #2a1845;
  background: linear-gradient(180deg, #fdfaf2, #f4ecdc);
}
.srl-pulled-title { margin: 0 0 12px; padding: 0 40px; text-align: center; font: 600 clamp(18px, 2.6vw, 26px)/1.25 var(--display); }
.srl-pulled-strip { display: flex; flex-wrap: wrap; justify-content: center; gap: 8px; margin: 0 0 16px; }
.srl-pill {
  min-height: 40px; padding: 0 16px; border-radius: 999px; cursor: pointer;
  border: 1px solid #d9c9ef; background: #efe6f8; color: #5b3a86;
  font: 600 12px/1 var(--display); letter-spacing: 0.12em; text-transform: uppercase;
}
.srl-pill-pulled { border-style: dashed; border-color: #c9a94e; background: transparent; color: #8a6a2a; }
.srl-pill-pulled.sel { border-style: solid; background: #fff; color: #2a1845; box-shadow: 0 0 0 2px rgba(201, 169, 78, 0.35); }
.srl-pulled-body { display: grid; grid-template-columns: minmax(96px, 200px) minmax(0, 1fr); gap: 16px 22px; align-items: start; }
.srl-pulled-card { margin: 0; text-align: center; }
.srl-pulled-art { display: block; width: 100%; padding: 0; border: 0; background: none; cursor: zoom-in; }
.srl-pulled-art img { display: block; width: 100%; aspect-ratio: 2 / 3; object-fit: cover; border-radius: 10px; border: 1px solid #c9a94e;
  box-shadow: 0 16px 28px -16px rgba(42, 24, 69, 0.8); }
.srl-pulled-art img.rev { transform: rotate(180deg); }
.srl-pulled-card figcaption { margin-top: 8px; font: 500 16px/1.3 var(--serif); color: #5b3a86; }
.srl-pulled-line { margin: 0 0 8px; font: 700 clamp(22px, 3vw, 30px)/1.15 var(--serif); }
.srl-pulled-for { margin: 0 0 12px; font: italic 500 17px/1.45 var(--serif); color: #7a6a8f; }
.srl-pulled-reading { margin: 0; font: 500 18px/1.55 var(--serif); color: #3d2f55; white-space: pre-line; }
@media (max-width: 699px) { .srl-pulled { padding-top: 50px; } }
@media (max-width: 520px) { .srl-pulled-body { grid-template-columns: 110px minmax(0, 1fr); gap: 12px 14px; } }
.srl-gift-rating { display: flex; justify-content: center; margin: 18px auto 8px; max-width: 560px; }
.srl-wrap { position: relative; width: 100%; }
/* The corner clearances below live here rather than inside SpreadReading:
   the account portrait exists on this page only, and the Studio preview
   shares that component. */
.srl-response { position: relative; }
.srl-close {
  /* Left of the account portrait (TopRightAccount), which holds the top-right
     corner at z-index 1000 at EVERY width, and level with it. Sitting at
     top-right/z-5, this button was underneath the old corner controls: tapping
     to close the reading opened the account menu instead, and the reading
     could not be closed at all. (It sat at right: 96px while two 36px icons
     held the corner; the one portrait needs --tra-clear-right, App.vue.) */
  position: absolute; z-index: 1001;
  top: calc(var(--tra-top, 10px) + (var(--tra-size, 42px) - 32px) / 2);
  right: var(--tra-clear-right, 64px);
  width: 32px; height: 32px; border-radius: 50%;
  background: #fff; color: #222; border: 1px solid #222;
  display: flex; align-items: center; justify-content: center; cursor: pointer;
}
/* A 44px hit area round the 32px ✕ (e2e phone walk 2026-10-30). */
.srl-close::after { content: ''; position: absolute; inset: -6px; border-radius: 50%; }
/* Wide enough that the reading (max 1024px, centred) ends well short of the
   account portrait: the close goes in the card's own corner. */
@media (min-width: 1240px) {
  .srl-close { top: 12px; right: 12px; }
}
/* The app pins the account portrait to the top-right corner of the viewport
   at z-index 1000. It is chrome, not content, and the reading used to render
   underneath the corner controls: the reading's own close button sat
   directly beneath the account menu at z-index 5, so tapping to close the
   reading opened the menu instead. There was no way out.

   So on every phone width (the app's own mobile breakpoint, not the narrower
   one this used to use — a 440px screen missed it entirely) the reading keeps
   clear of that band: its close button moves left of the portrait, and its
   question starts below the whole band. */
@media (max-width: 699px) {
  .srl-response :deep(.sr-hero) { padding-top: 46px; }
}
/* Tablets and small desktops (the reading spans the window, the question on
   one line across the top): a long question ran under the close button and up
   to the portrait. It keeps clear of the corner on both sides, so it stays
   centred. */
@media (min-width: 700px) and (max-width: 1239px) {
  .srl-response :deep(.sr-hero) { padding-left: 56px; padding-right: 56px; }
}
.srl-loading {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 26px; min-height: 60vh; padding: 28px 24px; text-align: center;
}
/* The drawn hand, floating in a soft glow while the reading weaves. */
.srl-loading-cards {
  /* Two per row so an even spread (4) lays out as a tidy 2×2 instead of a
     lopsided 3 + 1. The capped width admits exactly two cards; a trailing odd
     card (1- or 3-card) is centered by justify-content. */
  display: flex; flex-wrap: wrap; gap: clamp(10px, 2.4vw, 20px);
  justify-content: center; align-items: center;
  max-width: 280px; margin: 0 auto;
  filter: drop-shadow(0 0 46px rgba(195, 169, 255, 0.28));
}
.srl-loading-card {
  width: clamp(96px, 30vw, 124px); aspect-ratio: 2 / 3; border-radius: 11px; overflow: hidden;
  border: 2px solid rgba(184, 136, 74, 0.55); box-shadow: 0 10px 34px rgba(0, 0, 0, 0.5);
  animation: srl-card-float 2.8s ease-in-out infinite;
}
.srl-loading-card img { display: block; width: 100%; height: 100%; object-fit: cover; }
/* Rotate the IMAGE, not the card. The card carries srl-card-float, which
   animates `transform` — rotating the same element would be overwritten by every
   keyframe and the card would sit upright anyway. The chat surface hit the same
   trap with :hover; see the note on .card-image.reversed-card. */
.srl-loading-card.rev img { transform: rotate(180deg); }
.srl-loading-card-glyph {
  display: flex; align-items: center; justify-content: center; width: 100%; height: 100%;
  background: linear-gradient(160deg, #3a2566, #1c1030 60%, #2a1845); color: #f0d9a3; font-size: 26px;
}
@keyframes srl-card-float { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-9px); } }
.srl-loading-img {
  width: min(380px, 72vw); height: auto; border-radius: 18px;
  box-shadow: 0 0 80px rgba(195, 169, 255, 0.22);
  animation: srl-breathe 3.2s ease-in-out infinite;
}
@keyframes srl-breathe { 50% { transform: scale(1.03); opacity: 0.92; } }
.srl-loading-text {
  font-family: 'Cinzel', serif; font-style: italic; letter-spacing: 0.04em;
  font-size: clamp(15px, 2.2vw, 19px); color: rgba(212, 196, 232, 0.85); margin: 0;
}
.srl-illus-loading { max-width: 220px; width: 100%; height: auto; }
.srl-illus-loading-wrap { display: flex; flex-direction: column; align-items: center; gap: 12px; }
.srl-illus-loading-msg {
  margin: 0; text-align: center; font-family: 'Cinzel', serif; font-style: italic;
  letter-spacing: 0.04em; font-size: clamp(13px, 1.6vw, 16px); color: #6b3fa0;
}
.srl-msg-fade-enter-active, .srl-msg-fade-leave-active { transition: opacity 0.4s ease; }
.srl-msg-fade-enter-from, .srl-msg-fade-leave-to { opacity: 0; }

/* Paywall (rendered into SpreadReading's spotlight slot). */
.srl-paywall { display: flex; flex-direction: column; gap: 14px; align-items: flex-start; }
.srl-paywall-copy { font-size: 18px; line-height: 1.5; color: #5a4a72; margin: 0; font-style: italic; }
.srl-pay-btn {
  background: linear-gradient(180deg, #7b4bb0, #4e2776); color: #fff;
  border: none; border-radius: 999px; padding: 12px 22px; font-size: 0.95rem;
  font-weight: 600; cursor: pointer; box-shadow: 0 8px 20px -8px rgba(78, 39, 118, 0.6);
}
.srl-pay-btn:disabled { opacity: 0.6; cursor: default; }

/* Illustration. */
.srl-illus-btn { display: block; padding: 0; border: none; background: none; cursor: zoom-in; width: 100%; }
.srl-illus { display: block; width: 100%; border-radius: 10px; }

/* Expanded illustration. */
.srl-expanded { position: fixed; inset: 0; z-index: 1100; display: flex; align-items: center; justify-content: center; padding: 32px; cursor: zoom-out; }
.srl-expanded-overlay { position: absolute; inset: 0; background: rgba(8, 4, 20, 0.85); }
.srl-expanded-fig { position: relative; margin: 0; display: inline-block; line-height: 0; }
.srl-expanded-img { position: relative; display: block; max-width: 92vw; max-height: 88vh; border-radius: 12px; box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6); }
/* Caption layered over the bottom of the enlarged illustration: the picture's
   line in Letter Serif italic, cream, in its two beats (picture-caption
   DESIGN.md, 2026-10-30). It rises in just after the overlay. */
.srl-expanded-cap {
  position: absolute; left: 0; right: 0; bottom: 0; box-sizing: border-box;
  padding: 56px 28px 20px; text-align: center; text-wrap: balance;
  font: italic 500 clamp(17px, 1.7vw, 22px)/1.35 'Letter Serif', 'Cormorant Garamond', Georgia, serif;
  color: #fbf3e0; text-shadow: 0 1px 10px rgba(0, 0, 0, 0.55);
  background: linear-gradient(to top, rgba(8, 4, 20, 0.92), rgba(8, 4, 20, 0.55) 55%, rgba(8, 4, 20, 0));
  border-radius: 0 0 12px 12px;
  animation: srl-cap-in 520ms cubic-bezier(.2, .7, .2, 1) 260ms both;
}
@keyframes srl-cap-in { from { opacity: 0; transform: translateY(6px); } }
@media (prefers-reduced-motion: reduce) { .srl-expanded-cap { animation: none; } }

.fade-enter-active, .fade-leave-active { transition: opacity 0.3s ease; }
.fade-enter-from, .fade-leave-to { opacity: 0; }
</style>
